import {Button, Checkbox, Divider, Popover, Spin, Tooltip,} from 'antd';
import React, {useEffect, useState} from 'react';
import OperationMenu from '../OperationMenu';
import {CheckOperations, ComparisonOperations} from './Operations';
import {AppActions, useAppContext} from '../../context/AppContext';
import GrayButton from '../../components/GrayButton';
import SelectPlan from '../SelectPlan';
import {b} from 'plotly.js/src/traces/carpet/attributes';
import HecRasHelper from '../../helpers/HecRasHelper';
import ProjectInfo from '../ProjectInfo';
import {
    CaretRightOutlined,
    DotChartOutlined,
    ForwardOutlined,
    GlobalOutlined,
    PicLeftOutlined,
    ProfileOutlined,
    TableOutlined,
} from '@ant-design/icons';
import ApiService from '../../services/ApiService';
import {useUser} from '../../context/UserContext';
import {useHistory} from 'react-router-dom';
import PopoverScrollContent from '../../components/PopoverScrollContent';
import SelectionInfo from "./SelectionInfo";

export const OPS_STATUS = {
    NOT_CHECK: 0,
    CHECKING: 1,
    COMPLETED: 2,
    ERROR: 3,
}

export default function RunOperations({runType}) {
    const { appContext, dispatchProject, selectedHistoryId, selection, getSelectedHistory } =
        useAppContext();
    const {settings, user} = useUser();
    const history = useHistory();
    const [assoc, setAssoc] = useState(true);
    const [running, setRunning] = useState({
        text: '',
        running: false,
    });
    const [operations, setOperations] = useState([]);

    const getSelectionId = (selection) => {
        if (runType === 'Check') {
            return `${selection?.base?.plan?.id}_${selection?.base?.river?.key}`;
        } else {
            return `${selection?.base?.plan?.id}_${selection?.base?.river?.key}_${selection?.compare?.plan?.id}_${selection?.compare?.river?.key}`;
        }
    };

    const getInputData = ({_selection}) => {
        return {
            project: _selection.project.information,
            projection: _selection?.project?.projection,
            geometry: _selection.plan.geometry,
            flow: _selection.plan.flow,
            output: _selection.plan.mappedProfiles ?? [],
            plan: _selection.plan.plan,
            type: _selection.plan.type,
            profileNames: _selection.plan.mapProfiles.map((x) => {
                if (!x.targetName) x.targetName = x.target;

                return x;
            }),
            settings: user.settings,
            projectHash: _selection.projectHash,
            river: _selection.river?.river || null,
            reach: _selection.river?.reach || null,
            survey_geojson: _selection?.survey || null
        };
    };

    const batchRun = async () => {
        let histories = [...appContext?.history];
        for (const project of appContext?.projects) {
            for (const plan of project?.projectFiles) {
                if (plan?.rivers?.length > 0) {
                    for (const river of plan?.rivers) {
                        const _selection = {
                            base: {
                                project: {...project},
                                plan,
                                river,
                            },
                            compare: {},
                        };
                        dispatchProject({
                            type: AppActions.SET_PROJECT_SELECTION,
                            data: {selection: _selection},
                        });
                        const _ops = [
                            ...getOperations(_selection, getSelectionId(_selection), true),
                        ];
                        const data = await run(
                            histories,
                            _selection,
                            _ops,
                            (ops) => {
                                setOperations([...ops]);
                            }
                        );

                        dispatchProject({
                            type: AppActions.SET_SELECTED_HISTORY_ID,
                            data: {id: data.id}
                        })
                    }
                }
            }
        }

        dispatchProject({
            type: AppActions.SET_HISTORY,
            data: {
                history: histories,
            },
        });


    };

    const run = async (
        histories,
        selection,
        _operations,
        onUpdatedOperations
    ) => {
        let parsed = {};
        setRunning({
            running: true,
            text: 'Project Preparing...',
        });

        let intersection_child_key = [
            'ST1',
            'ST2',
            'XY2',
            'XY4',
            'XY5',
            'XY6',
            'XY7',
        ];
        let calc_intersection = false;
        let include_survey_data = false

        for (const operation of _operations) {
            for (const child of operation.children) {
                if (
                    intersection_child_key.includes(child.key_text) &&
                    child?.enable
                ) {
                    calc_intersection = true;
                }

                if (child.type === 'survey' && child.enable) {
                    include_survey_data = true
                }
            }
            if (calc_intersection) break;
        }

        let interval = null;
        let timer = 0;
        interval = setInterval(() => {
            timer++;
            if (timer <= 3) {
                setRunning({
                    running: true,
                    text: `Project Preparing... (${timer} s)`,
                });
            } else {
                setRunning({
                    running: true,
                    text: `Spatial pre-calculations in progress... (${timer} s)`,
                });
            }
        }, 1000);

        if (runType === 'Check') {
            const inputData = getInputData({_selection: selection.base})

            const res = await ApiService.RasCheck.ParseInternal({
                ...inputData,
                calc_intersection,
            });
            if (res.success) parsed = JSON.parse(res.data);
        } else {
            const data = {
                selected: getInputData({_selection: selection.base}),
                target: getInputData({_selection: selection.compare}),
                settings: user.settings,
                isAssoc: assoc,
                calc_intersection,
            };
            const res = await ApiService.RasCheck.ParseCompare(data);
            if (res.success) parsed = JSON.parse(res.data);
        }

        clearInterval(interval);
        setRunning({
            running: true,
            text: `Spatial pre-calculations are completed in ${timer} seconds.`,
        });

        let req = ApiService.RasCheck.InternalCheck;
        let input = {
            key: parsed.key,
            projectHash: selection.base.plan.projectHash,
        };
        if (runType === 'Comparison') {
            req = ApiService.RasCheck.CompareCheck;
            input = {
                key: parsed.key,
                baseHash: selection.base.plan.projectHash,
                compareHash: selection.compare.plan.projectHash,
            };
        }

        for (const operation of _operations) {
            for (const child of operation.children) {
                try {
                    if (!child.enable) continue;

                    child.status = OPS_STATUS.CHECKING;
                    onUpdatedOperations &&
                    onUpdatedOperations([..._operations]);
                    let time = new Date();
                    const res = await req({
                        ...input,
                        target: child.request,
                    });

                    let time2 = new Date();
                    const sec = (time2.getTime() - time.getTime()) / 1000;
                    if (res.success) {
                        child.status = OPS_STATUS.COMPLETED;
                        child.time = sec;
                        child.data = JSON.parse(res.data);
                        child.originalData = JSON.parse(res.data);
                        child.enable = false;
                        child.hasError = child?.data?.result?.find(
                            (x) => x?.data?.length > 0
                        );
                    } else {
                        child.status = OPS_STATUS.ERROR;
                        child.time = sec;
                    }
                    onUpdatedOperations &&
                    onUpdatedOperations([..._operations]);
                } catch (exp) {
                    child.status = OPS_STATUS.ERROR;
                    onUpdatedOperations &&
                    onUpdatedOperations([..._operations]);
                    console.error('Error: ', exp);
                }
            }
        }

        let _wsel = {};
        if (runType === 'Comparison') {
            const findResult = (_operations, key) => {
                let res = {};
                _operations.forEach((op) => {
                    const x = op.children.find((x) => x.key === key);
                    if (x) {
                        res = x;
                    }
                });
                return res;
            };

            try {
                const data = {
                    C11: findResult(_operations, 'C11') || [-1, -1], // C.11
                    C1: findResult(_operations, 'C1') || {}, // C.1
                    C2: findResult(_operations, 'C2') || {}, //C.2
                    C3: findResult(_operations, 'C3') || {}, //C.3
                    C4: findResult(_operations, 'C4') || {}, // C.4
                    C5: findResult(_operations, 'C5') || {}, // C.5
                    C6: findResult(_operations, 'C6') || {}, // C.6
                    C7: findResult(_operations, 'C7') || {}, // C.7
                    C12: findResult(_operations, 'C12') || {}, // C.12
                    C13: findResult(_operations, 'C13') || {}, // C.13
                    C15: findResult(_operations, 'C15') || {}, // C.15
                    C20: findResult(_operations, 'C20') || {}, // C.12
                    C21: findResult(_operations, 'C21') || {}, // C.13
                };

                _wsel = HecRasHelper.calcWSEL({
                    ...data,
                    minCS: Math.min(
                        ...(parsed?.summary?.compare?.cs_list || []).map(
                            (x) => x.value
                        )
                    ),
                    maxCS: Math.max(
                        ...(parsed?.summary?.compare?.cs_list || []).map(
                            (x) => x.value
                        )
                    ),
                    csList: parsed?.summary?.compare?.cs_list || [],
                });
            } catch (exp) {
                console.log(exp);
            }

            setRunning({
                running: false,
                text: '',
            });
        }

        const del = await ApiService.RasCheck.RunComplete(parsed.key);
        setOperations([..._operations]);

        const id = getSelectionId(selection);

        const index = histories.findIndex((x) => x.id === id);
        if (index > -1) {
            histories[index] = {
                ...histories[index],
                date: new Date(),
                operations: [..._operations],
                summary: parsed.summary,
                calculation_tolerances: parsed.calculation_tolerances,
                mapBase: parsed.map_base,
                mapCompare: parsed.map_cmp,
                associate: parsed.associate,
                wsel: _wsel,
            };
        } else {
            histories.push({
                id,
                type: runType,
                selection: {...selection},
                date: new Date(),
                operations: [..._operations],
                summary: parsed.summary,
                calculation_tolerances: parsed.calculation_tolerances,
                mapBase: parsed.map_base,
                mapCompare: parsed.map_cmp,
                wsel: _wsel,
            });
        }

        setRunning({
            running: false,
            text: '',
        });

        return {histories, id};
    };

    const getOperations = (selection, historyId, selected) => {
        const history = appContext?.history?.find((x) => x.id === historyId);

        let _operations = [];
        if (history)
            _operations = history?.operations || [];
        else if (runType === 'Check') {
            _operations = JSON.parse(JSON.stringify(CheckOperations));
        } else {
            _operations = JSON.parse(JSON.stringify(ComparisonOperations));
        }

        return _operations.map((op) => {
            op.children.forEach((child) => {
                child.enable = selected;

                if (
                    ['output', 'floadway', 'yr100', 'library'].includes(
                        child.type
                    ) &&
                    !hasOutput()
                ) {
                    child.enable = false;
                    child.disabled = true;
                }
                if (child.type === 'floadway' && !hasFloadway(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    // op.disabled = true
                } else if (child.type === 'yr100' && !has100yr(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    // op.disabled = true
                } else if (child.type === 'library' && !isLibraryPlan(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    op.disabled = true;
                } else if (child.type === 'structure' && !hasStructure(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    op.disabled = true;
                } else if (child.type === 'schematic' && !hasGeoReference(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    op.disabled = true;
                } else if (child.type === 'survey' && !hasSurvey(selection)) {
                    child.enable = false;
                    child.disabled = true;
                    op.disabled = true;
                }
            });

            return op;
        });
    };

    const isLibraryPlan = (selection) => {
        if (runType === 'Check') {
            return HecRasHelper.isLibraryPlan(selection.base.plan);
        } else {
            return (
                HecRasHelper.isLibraryPlan(selection.base.plan) &&
                HecRasHelper.isLibraryPlan(selection.compare.plan)
            );
        }
    };

    const hasGeoReference = (selection) => {
        if (runType === 'Check') {
            return HecRasHelper.hasGeoReferenced(
                selection.base.plan?.geometry?.text
            );
        } else {
            return (
                HecRasHelper.hasGeoReferenced(
                    selection.base.plan?.geometry?.text
                ) &&
                HecRasHelper.hasGeoReferenced(
                    selection.compare.plan?.geometry?.text
                )
            );
        }
    };

    const hasSurvey = (selection) => {
        if (runType === 'Check') {
            const survey = selection?.base?.project?.shapeFiles?.find(x => x.hasSurveyData)
            if (survey)
                return true;
        } else {
            const survey_base = selection?.base?.project?.shapeFiles?.find(x => x.hasSurveyData)
            const survey_cmp = selection?.compare?.project?.shapeFiles?.find(x => x.hasSurveyData)
            if (survey_base && survey_cmp)
                return true
        }

        return false
    };

    const hasStructure = (selection) => {
        if (runType === 'Check') {
            return (
                HecRasHelper.getStructureList(
                    selection.base.plan?.geometry?.text,
                    selection.base?.river?.river,
                    selection.base?.river?.reach
                )?.length > 0
            );
        } else {
            return (
                HecRasHelper.getStructureList(
                    selection.base.plan?.geometry?.text,
                    selection.base?.river?.river,
                    selection.base?.river?.reach
                ) &&
                HecRasHelper.getStructureList(
                    selection.compare.plan?.geometry?.text,
                    selection.compare?.river?.river,
                    selection.compare?.river?.reach
                )
            );
        }
    };

    const hasFloadway = (selection) => {
        if (runType === 'Check') {
            return selection?.base?.plan?.mapProfiles?.find(
                (x) => x.target === 'FW'
            );
        } else {
            return (
                selection?.base?.plan?.mapProfiles?.find(
                    (x) => x.target === 'FW'
                ) &&
                selection?.compare?.plan?.mapProfiles?.find(
                    (x) => x.target === 'FW'
                )
            );
        }
    };

    const hasOutput = () => {
        if (runType === 'Check') {
            return (
                selection?.base?.project?.information?.hasOutputFile &&
                selection?.base?.plan?.isFindOutput !== 'false'
            );
        } else {
            return (
                selection?.base?.project?.information?.hasOutputFile &&
                selection?.base?.plan?.isFindOutput !== 'false' &&
                selection?.compare?.project?.information?.hasOutputFile &&
                selection?.compare?.plan?.isFindOutput !== 'false'
            );
        }
    };

    const has100yr = (selection) => {
        if (runType === 'Check') {
            return selection?.base?.plan?.mapProfiles?.find(
                (x) => x.target === 'YR100'
            );
        } else {
            return (
                selection?.base?.plan?.mapProfiles?.find(
                    (x) => x.target === 'YR100'
                ) &&
                selection?.compare?.plan?.mapProfiles?.find(
                    (x) => x.target === 'YR100'
                )
            );
        }
    };

    const isOperationSelectedAll = () => {
        for (const operation of operations) {
            for (const child of operation?.children || []) {
                if (!child.enable && !child.disabled) return false;
            }
        }

        return true;
    };

    const findHistory = () => {
        return appContext?.history?.find(
            (x) => x.id === getSelectionId(selection)
        );
    };

    const getFirstErrorMessage = () => {
        const historyData = findHistory();
        if (historyData) {
            for (const operation of historyData.operations) {
                for (const child of operation.children) {
                    if (child.data && child?.data?.result?.length > 0) {
                        return child;
                    }
                }
            }
        }
    };

    useEffect(() => {
        if (selection.base.plan || selection.compare.plan) {
            setOperations([...getOperations(selection, getSelectionId(selection), true)]);
        }
    }, [
        selection.base.plan?.id,
        selection?.base?.river?.key,
        selection.compare.plan?.id,
        selection?.compare?.river?.key,
        runType,
    ]);

    return (
        <div>
            <Button
                disabled={running.running}
                size={'small'}
                type={'default'}
                style={{marginBottom: 16}}
                onClick={() => {
                    const histories = appContext?.history;
                    const results = []

                    const missing_messages = []
                    for (const history of histories) {
                        console.log('history', history)
                        const messages = []
                        const operations = history.operations
                        for (const operation of operations) {
                            const children = operation.children
                            for (const child of children) {
                                if(child?.originalData?.result?.length > 0 && child?.originalData?.ai_result?.messages?.length === 0) {
                                    missing_messages.push(child)
                                }

                                if (child.originalData?.ai_result?.messages?.length > 0) {
                                    for (const element of child.originalData?.ai_result?.messages) {
                                        messages.push(element)
                                    }
                                }
                            }
                        }

                        let report = {
                            missing_messages,
                            type: history.type,
                            base_project: {
                                project: {
                                    title: history.selection.base.project.information.title,
                                    descriptions: history.selection.base.project.information.descriptions,
                                },
                                plan: {
                                    title: history.selection.base.plan.plan.title,
                                    version: history.selection.base.plan.plan.version,
                                },
                                river: {
                                    river: history.selection.base.river?.river || null,
                                    reach: history.selection.base.river?.reach || null,
                                },
                                summary: {
                                    river_list: history.summary?.base?.river_list || [],
                                    length: history.summary?.base?.length || 0,
                                    crossSections: history.summary?.base?.crossSections || 0,
                                    bridges: history.summary?.base?.bridges || 0,
                                    single_bridges: history.summary?.base?.single_bridges || 0,
                                    multiple_bridges: history.summary?.base?.multiple_bridges || 0,
                                    inline: history.summary?.base?.inline || 0,
                                    culverts: history.summary?.base?.culverts || 0,
                                    profiles: history.summary?.base?.profiles || 0,
                                    flowLocations: history.summary?.base?.flowLocations || 0,
                                    calculation_tolerances: history.summary?.base?.calculation_tolerances
                                },
                            }
                        }

                        if (report.type === 'Comparison') {
                            report.compare_project = {
                                project: {
                                    title: history.selection?.compare?.project?.information?.title,
                                    descriptions: history.selection?.compare?.project?.information?.descriptions,
                                },
                                plan: {
                                    title: history.selection?.compare?.plan?.plan?.title,
                                    version: history.selection?.compare?.plan?.plan?.version,
                                },
                                river: {
                                    river: history.selection?.compare?.river?.river || null,
                                    reach: history.selection?.compare?.river?.reach || null,
                                },
                                summary: {
                                    river_list: history.summary?.compare?.river_list || [],
                                    length: history.summary?.compare?.length || 0,
                                    crossSections: history.summary?.compare?.crossSections || 0,
                                    bridges: history.summary?.compare?.bridges || 0,
                                    single_bridges: history.summary?.compare?.single_bridges || 0,
                                    multiple_bridges: history.summary?.compare?.multiple_bridges || 0,
                                    inline: history.summary?.compare?.inline || 0,
                                    culverts: history.summary?.compare?.culverts || 0,
                                    profiles: history.summary?.compare?.profiles || 0,
                                    flowLocations: history.summary?.compare?.flowLocations || 0,
                                    calculation_tolerances: history.summary?.base?.calculation_tolerances
                                },
                            }
                        }

                        report.messages = messages

                        results.push(report)
                    }


                    setTimeout(() => {
                        console.log('results', results)

                        const filename = `ai_results_${new Date().toISOString().replaceAll(':', '').split('.')[0]}.json`
                        const jsonString = JSON.stringify(results, null, 2);
                        const blob = new Blob([jsonString], { type: "application/json" });
                        const url = URL.createObjectURL(blob);

                        const a = document.createElement("a");
                        a.href = url;
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);

                        URL.revokeObjectURL(url);
                    }, [1000])
                }}
            >
                Download Json
            </Button>
            <div
                style={{
                    marginBottom: 8,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                }}
            >
                <div style={{flex: 1}}>
                    <Popover
                        placement={'bottomLeft'}
                        trigger={'click'}
                        content={
                            <PopoverScrollContent maxHeight={600}>
                                <SelectPlan type={'base'}/>
                            </PopoverScrollContent>
                        }
                    >
                        <SelectionInfo type={'base'} />
                    </Popover>
                    <br/>
                    <ProjectInfo
                        autoHeight={true}
                        showNotes={true}
                        folderName={
                            selection?.base.project?.information?.folderName
                        }
                        title={selection?.base.project?.information?.title}
                        descriptions={
                            selection?.base.project?.information?.descriptions
                        }
                        planTitle={selection?.base.plan?.plan?.title}
                        version={
                            selection?.base.project?.projectFiles &&
                            selection.base.project?.projectFiles?.length > 0 &&
                            selection?.base.plan?.plan?.version
                        }
                    />
                </div>
                {runType === 'Comparison' && (
                    <div style={{flex: 1}}>
                        <Popover
                            placement={'bottomLeft'}
                            trigger={'click'}
                            content={
                                <PopoverScrollContent maxHeight={600}>
                                    <SelectPlan type={'compare'}/>
                                </PopoverScrollContent>
                            }
                        >
                            <SelectionInfo type={'compare'} />
                        </Popover>
                        <ProjectInfo
                            autoHeight={true}
                            showNotes={true}
                            folderName={
                                selection.compare.project?.information
                                    ?.folderName
                            }
                            title={
                                selection.compare.project?.information?.title
                            }
                            descriptions={
                                selection.compare.project?.information
                                    ?.descriptions
                            }
                            planTitle={selection.compare.plan?.plan?.title}
                            version={
                                selection.compare.project?.projectFiles &&
                                selection.compare.project?.projectFiles
                                    ?.length > 0 &&
                                selection.compare.plan?.plan?.version
                            }
                        />
                    </div>
                )}
            </div>

            <Divider/>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 8,
                }}
            >
                <Button
                    disabled={running.running}
                    size={'small'}
                    style={{width: 100}}
                    onClick={() => {
                        setOperations([
                            ...getOperations(
                                selection,
                                getSelectionId(selection),
                                !isOperationSelectedAll()
                            ),
                        ]);
                    }}
                >
                    {isOperationSelectedAll() ? 'Deselect All' : 'Select All'}
                </Button>
                <Button.Group>
                    <Button
                        loading={running.running}
                        disabled={running.running}
                        size={'small'}
                        type={'primary'}
                        icon={<CaretRightOutlined/>}
                        onClick={async () => {
                            const {histories, id} = await run(
                                [...appContext?.history],
                                selection,
                                operations,
                                (ops) => setOperations([...ops])
                            );
                            
                            dispatchProject({
                                type: AppActions.SET_HISTORY,
                                data: {
                                    history: histories,
                                },
                            });

                            dispatchProject({
                                type: AppActions.SET_SELECTED_HISTORY_ID,
                                data: {
                                    id: id,
                                },
                            });
                        }}
                    />
                    {runType === 'Check' && (
                        <Button
                            loading={running.running}
                            disabled={running.running}
                            size={'small'}
                            type={'primary'}
                            style={{marginLeft: 4}}
                            icon={<ForwardOutlined/>}
                            onClick={batchRun}
                        />
                    )}
                </Button.Group>
                <Button.Group>
                    <Tooltip title={'Go To Overview'}>
                        <Button
                            disabled={!findHistory() || running.running}
                            size={'small'}
                            type={'default'}
                            icon={<ProfileOutlined/>}
                            onClick={() => {
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        tab: 'Message',
                                    },
                                });

                                history.push('/app/messages');
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={'Go To Message'}>
                        <Button
                            disabled={!findHistory() || running.running}
                            size={'small'}
                            icon={<TableOutlined/>}
                            type={'default'}
                            onClick={() => {
                                const item = getFirstErrorMessage();
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        operation: item,
                                        tab: 'Message',
                                    },
                                });
                                history.push('/app/messages');
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={'Go To Plot'}>
                        <Button
                            disabled={!findHistory() || running.running}
                            size={'small'}
                            icon={<DotChartOutlined/>}
                            type={'default'}
                            onClick={() => {
                                const item = getFirstErrorMessage();
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        operation: item,
                                        tab: 'Plot',
                                    },
                                });
                                history.push('/app/messages');
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={'Go To ui'}>
                        <Button
                            disabled={!findHistory() || running.running}
                            size={'small'}
                            icon={<PicLeftOutlined/>}
                            type={'default'}
                            onClick={() => {
                                const item = getFirstErrorMessage();
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        operation: item,
                                        tab: 'UI',
                                    },
                                });
                                history.push('/app/messages');
                            }}
                        />
                    </Tooltip>
                    <Tooltip title={'Go To Map'}>
                        <Button
                            disabled={
                                !findHistory() ||
                                running.running ||
                                !(
                                    selection?.base?.project?.projection
                                        ?.text ||
                                    selection?.compare?.project?.projection
                                        ?.text
                                ) ||
                                !hasGeoReference(selection)
                            }
                            size={'small'}
                            icon={<GlobalOutlined/>}
                            type={'default'}
                            onClick={() => {
                                const item = getFirstErrorMessage();
                                dispatchProject({
                                    type: AppActions.PUSH_OPERATION_MESSAGE,
                                    data: {
                                        id: getSelectionId(selection),
                                        operation: item,
                                        tab: 'Map',
                                    },
                                });
                                history.push('/app/messages');
                            }}
                        />
                    </Tooltip>
                </Button.Group>
                {runType === 'Comparison' && (
                    <Checkbox
                        checked={assoc}
                        onChange={(e) => setAssoc(e.target.checked)}
                    >
                        Associate Structure
                    </Checkbox>
                )}
                <Spin spinning={running.running} size={'small'}/>
                {running.text}
            </div>
            <OperationMenu
                onClick={(item) => {
                    if (
                        !appContext?.history?.find(
                            (x) => x.id === getSelectionId(selection)
                        ) ||
                        running.running
                    )
                        return;

                    dispatchProject({
                        type: AppActions.SET_SELECTED_HISTORY_ID,
                        data: {
                            id: getSelectionId(selection),
                            operation: item,
                        },
                    });
                    history.push('/app/messages');
                }}
                operations={operations}
                onCheckedChanged={(items, val) => {
                    const _operations = [...operations];
                    items.forEach((item) => {
                        _operations.forEach((op) => {
                            op.children.forEach((child) => {
                                if (child.title === item.title)
                                    child.enable = val;
                            });
                        });
                    });

                    setOperations(_operations);
                }}
            />
        </div>
    );
}
